import storage from '../../services/storage'
import { buildTicketsFromZone } from '../../shared/helpers/cart-builder'
import {
  hasMemberPricing,
  hasYouthTickets,
  isMembershipType
} from '../../shared/helpers/logic'
import {
  includesIllinois,
  includesChicago,
  ticketMatches,
  onSameDay,
  notMembershipType
} from './logic'
/*
  This module is where all the logic for building our "cart"
  representation for the tickets-app lives
*/

const findTicketWith = (ageGroup, label, member, tickets) => {
  return tickets.find(
    ticket =>
      ticket.ageGroup === ageGroup &&
      ticket.member === member &&
      ticket.label.toLowerCase() === label.toLowerCase()
  )
}

const addYouthTickets = (tickets, member, label, ticketSum) => {
  const youthTicket = findTicketWith('youth', label, member, tickets)
  const youthQuantity = youthTicket ? youthTicket.quantity : 0
  return ticketSum + youthQuantity
}

export const buildTicketsFromPricing = (
  pricing,
  existingTickets = null,
  chosenZone = null
) => {
  const { member, nonMember } = pricing
  const memberTickets = member
    ? buildTicketsFromZone(member, existingTickets, chosenZone)
    : []
  const nonMemberTickets = nonMember
    ? buildTicketsFromZone(nonMember, existingTickets, chosenZone)
    : []
  return [...memberTickets, ...nonMemberTickets]
}

export const getCartItem = (cartItems, event) => {
  return cartItems.filter(notMembershipType).find(item => {
    if (!onSameDay(item, event)) return false
    return item.externalId === event.id || item.productId === event.productId
  })
}

export const buildCartEvent = (items, event) => {
  if (!event) return null
  // If a zone was selected, then use the pricing associated with that, otherwise use pricing at event level.
  let pricing = event.chosenZone ? event.chosenZone.pricing : event.pricing
  const cartItem = getCartItem(items, event)
  const existingTickets = cartItem && cartItem.tickets
  const tickets = buildTicketsFromPricing(
    pricing,
    existingTickets,
    event.chosenZone
  )
  return { ...event, tickets }
}

export const getCurrentChosenEvent = (
  id,
  events,
  manualUpdateTicketsNeeded = 0
) => {
  if (!id) return null
  let event = events.find(eventInstance => eventInstance.id === Number(id))
  if (!event) {
    for (let i = 0; i < events.length; i++) {
      const scheduledEvent = events[i].times.find(
        scheduledEvent => scheduledEvent.id === Number(id)
      )

      if (scheduledEvent) {
        event = {
          ...events[i],
          ...scheduledEvent
        }
        break
      }
    }
  }

  // Find the zone that was chosen and attach it to the event for continuing to do other processing.
  let chosenZoneId = storage.getItem('chosenZoneId')
  if (chosenZoneId) {
    let zone = event.performances[0].zones.find(z => z.zoneId == chosenZoneId)
    if (zone) {
      event.chosenZone = zone
    }
  }

  // If applicable pull out the pricing associated with a particular zone.
  const { member, nonMember } = event.chosenZone
    ? event.chosenZone.pricing
    : event.pricing
  const memberTickets = member
    ? buildTicketsFromZone(member, event.tickets, event.chosenZone)
    : []
  const nonMemberTickets = nonMember
    ? buildTicketsFromZone(nonMember, event.tickets, event.chosenZone)
    : []
  event.tickets = [...memberTickets, ...nonMemberTickets]
  event.availability = event.availability + manualUpdateTicketsNeeded

  return event
}

export const getSessionCartData = () => {
  const day = storage.getItem('day')
  const step = storage.getItem('step')
  const chosenEventId = storage.getItem('chosenEventId')
  const chosenZoneId = storage.getItem('chosenZoneId')
  const fromEventId = storage.getItem('fromEventId')
  const minimumAvailability = storage.getItem('minimumAvailability')

  return {
    day,
    step,
    chosenEventId,
    chosenZoneId,
    fromEventId,
    minimumAvailability
  }
}

export const addNecessaryTickets = (event, dependentTickets, eventMinimums) => {
  const ticketsWithQuantity = dependentTickets.filter(
    ticket => ticket.quantity !== 0
  )
  const needToAddYouth =
    hasYouthTickets(ticketsWithQuantity) && !hasYouthTickets(event.tickets)
  const newTickets = event.tickets.map(newEventTicket => {
    let { ageGroup, label, member } = newEventTicket
    const minimumQuantity =
      eventMinimums[member ? 'member' : 'nonMember'][ageGroup]
    let youthTransferAgeGroup = member ? 'child' : 'adult'
    let newQuantity = newEventTicket.quantity
    let youthQuantity = 0
    const dependentTicket = findTicketWith(
      ageGroup,
      label,
      member,
      ticketsWithQuantity
    )
    if (needToAddYouth && ageGroup === youthTransferAgeGroup) {
      youthQuantity = addYouthTickets(ticketsWithQuantity, member, label, 0)
    }

    if (
      dependentTicket &&
      minimumQuantity < dependentTicket.quantity + youthQuantity
    ) {
      newQuantity = dependentTicket.quantity + youthQuantity
    } else if (
      youthQuantity !== 0 &&
      minimumQuantity === 0 &&
      newEventTicket.quantity === 0
    ) {
      newQuantity = minimumQuantity + youthQuantity
    } else if (minimumQuantity < newEventTicket.quantity) {
      newQuantity = minimumQuantity
    }

    return {
      ...newEventTicket,
      quantity: newQuantity
    }
  })

  return { ...event, tickets: newTickets }
}

export const prepareItemsForCart = items => {
  const newItems = items.map(item => {
    if (isMembershipType(item)) {
      return item
    }
    return {
      ...item,
      tickets: item.tickets.filter(ticket => ticket.quantity !== 0)
    }
  })
  return newItems
}

export const combineTickets = (mainTickets, ticketsToTransfer) => {
  if (
    (includesIllinois(mainTickets) && includesIllinois(ticketsToTransfer)) ||
    (includesChicago(mainTickets) && includesIllinois(ticketsToTransfer))
  ) {
    return ticketsToTransfer
  }
  const transferKey = includesIllinois(ticketsToTransfer)
    ? 'illinois'
    : 'chicago'
  const mainKey = includesIllinois(mainTickets) ? 'illinois' : 'chicago'
  return ticketsToTransfer
    .filter(ticket => ticket.quantity !== 0)
    .map(ticket => {
      let { ageGroup, member, pricingGroup } = ticket
      pricingGroup = pricingGroup === transferKey ? mainKey : pricingGroup
      let transferTicket = mainTickets.find(mTicket =>
        ticketMatches(mTicket, ageGroup, member, pricingGroup)
      )
      if (!transferTicket) {
        transferTicket = ticket
      }
      return { ...transferTicket, quantity: ticket.quantity }
    })
}

export const getAddOnsInNeedOfRestore = (ogAddOns, cartItems) => {
  return ogAddOns.filter(ogAddOn => {
    const currentCartAddOn = cartItems.find(
      item => item.externalId === ogAddOn.externalId
    )
    if (!currentCartAddOn) {
      return true
    }
    const ticketsMatch = currentCartAddOn.tickets.every(ticket => {
      let originalTicket = ogAddOn.tickets.findIndex(
        ogTicket =>
          ogTicket.priceTypeId === ticket.priceTypeId &&
          ogTicket.quantity === ticket.quantity
      )
      return originalTicket !== -1
    })
    return !ticketsMatch
  })
}

export const getManualUpdateAddons = (
  events,
  addOnItemIds,
  neededAvailability
) => {
  let manualUpdateAddOns = events.filter(event => {
    if (event.times.length !== 0) {
      return event.times.some(time => addOnItemIds.includes(time.id))
    } else {
      return addOnItemIds.includes(event.id)
    }
  })

  // filter out any add-ons which don't have member pricing
  manualUpdateAddOns = manualUpdateAddOns.filter(hasMemberPricing)

  return manualUpdateAddOns.map(event => {
    if (event.times.length !== 0) {
      let time = event.times.find(time => addOnItemIds.includes(time.id))
      if (time.availability < neededAvailability) {
        time.availability = neededAvailability
      }
      return { ...event, times: [time] }
    } else if (event.availability < neededAvailability) {
      return { ...event, availability: neededAvailability }
    } else {
      return event
    }
  })
}
